import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Shield, Tag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { shopApi, shopError } from "../lib/shop";
import { startCashfreePayment } from "../lib/cashfree";
import { formatINR } from "../lib/constants";
import { ButtonRing } from "../components/LoadingRing";

const initial = {
    full_name: "", phone: "", email: "",
    address_line1: "", address_line2: "",
    city: "", state: "", pincode: "", country: "India",
};

export default function Checkout() {
    const { items, clear } = useCart();
    const { customer, loginWithGoogle } = useCustomerAuth();    const navigate = useNavigate();
    const [form, setForm] = useState(initial);
    const [coupon, setCoupon] = useState("");
    const [applied, setApplied] = useState("");
    const [quote, setQuote] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!customer) return;
        setForm((f) => ({
            ...f,
            full_name: f.full_name || customer.name || "",
            email: f.email || customer.email || "",
            phone: f.phone || customer.phone || "",
            ...(customer.addresses?.[0] && !f.address_line1 ? customer.addresses[0] : {}),
        }));
    }, [customer]);

    const refreshQuote = useCallback(async (code) => {
        if (items.length === 0) { setQuote(null); return; }
        try {
            const { data } = await shopApi.post("/quote", {
                items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
                coupon_code: code || null,
                email: form.email || undefined,
            });
            setQuote(data);
            if (code && data.coupon_error) {
                toast.error(data.coupon_error);
                setApplied("");
            } else if (code && data.coupon) {
                setApplied(data.coupon.code);
                toast.success(`${data.coupon.name} applied`);
            }
        } catch (e) {
            toast.error(shopError(e));
        }
    }, [items, form.email]);

    useEffect(() => { refreshQuote(applied || null); }, [items.length, applied]); // eslint-disable-line

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (items.length === 0) { toast.error("Cart is empty"); return; }
        setSubmitting(true);
        try {
            const { data: order } = await shopApi.post("/checkout", {
                items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
                address: form,
                coupon_code: applied || null,
                payment_method: "cashfree",
                notes: "",
            });
            sessionStorage.setItem("rhm_pending_order", order.order_number);
            await startCashfreePayment(order.order_number);
        } catch (err) {
            toast.error(shopError(err));
            setSubmitting(false);
        }
    };

    const subtotal = quote?.subtotal ?? 0;
    const discount = quote?.discount ?? 0;
    const shipping = quote?.shipping ?? 0;
    const total = quote?.total ?? 0;
    const digitalOnly = items.length > 0
        && items.every(({ product }) => (product.shop_type || "product") !== "product");

    return (
        <div className="pt-28 md:pt-32 pb-20" data-testid="checkout-page">
            <div className="mx-auto max-w-6xl px-6 md:px-12">
                <Link to="/shop" className="inline-flex items-center gap-2 text-zinc-500 hover:text-gold text-sm">
                    <ChevronLeft className="w-4 h-4" /> Continue Shopping
                </Link>
                <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight mt-4 mb-3">Checkout</h1>
                {!customer && (
                    <div className="mb-8 max-w-md" data-testid="checkout-guest-block">
                        <button type="button" onClick={loginWithGoogle} data-testid="checkout-google-button"
                            className="w-full btn-pill btn-secondary !py-3.5">
                            CONTINUE WITH GOOGLE
                        </button>
                        <p className="text-sm text-zinc-500 mt-3">
                            Or <Link to="/account/login" className="text-gold" data-testid="checkout-signin-link">sign in
                            with email</Link> to save your details and track this order — you can also continue as a guest
                            below.
                        </p>
                    </div>
                )}

                <div className="grid lg:grid-cols-12 gap-10">
                    <form onSubmit={submit} className="lg:col-span-7 space-y-5" data-testid="checkout-form">
                        <h2 className="font-display text-xl uppercase tracking-tight text-gold">
                            {digitalOnly ? "Your Details" : "Shipping Address"}
                        </h2>
                        {digitalOnly && (
                            <p className="text-sm text-zinc-500" data-testid="checkout-digital-note">
                                Nothing is shipped. Your guide appears under My Downloads in your account, and
                                consultation packs are booked from your account.
                            </p>
                        )}

                        <div className="grid sm:grid-cols-2 gap-4">
                            <Input name="full_name" label="Full Name" value={form.full_name} onChange={onChange} required testid="ck-full-name" />
                            <Input name="phone" label="Phone" value={form.phone} onChange={onChange} required testid="ck-phone" />
                        </div>
                        <Input name="email" type="email" label="Email" value={form.email} onChange={onChange} required testid="ck-email" />
                        {!digitalOnly && (
                            <>
                                <Input name="address_line1" label="Address Line 1" value={form.address_line1} onChange={onChange} required testid="ck-addr1" />
                                <Input name="address_line2" label="Address Line 2 (optional)" value={form.address_line2} onChange={onChange} testid="ck-addr2" />
                                <div className="grid sm:grid-cols-3 gap-4">
                                    <Input name="city" label="City" value={form.city} onChange={onChange} required testid="ck-city" />
                                    <Input name="state" label="State" value={form.state} onChange={onChange} required testid="ck-state" />
                                    <Input name="pincode" label="Pincode" value={form.pincode} onChange={onChange} required testid="ck-pincode" />
                                </div>
                            </>
                        )}

                        <h2 className="font-display text-xl uppercase tracking-tight text-gold mt-8">Payment</h2>
                        <div className="p-5 bg-bg1 border border-gold/40" data-testid="payment-cashfree">
                            <div className="flex items-start gap-3">
                                <input type="radio" checked readOnly className="accent-gold mt-1" />
                                <div>
                                    <div className="font-bold uppercase tracking-widest text-sm">Pay Online — Secure</div>
                                    <div className="text-xs text-zinc-400 mt-1">
                                        UPI, cards, net banking and wallets. Payments are handled by Cashfree —
                                        we never see your card details.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="p-5 bg-bg1 border border-white/10 opacity-50" data-testid="payment-cod-soon">
                            <div className="flex items-start gap-3">
                                <input type="radio" disabled className="mt-1" />
                                <div>
                                    <div className="font-bold uppercase tracking-widest text-sm">
                                        Cash on Delivery — Coming Soon
                                    </div>
                                    <div className="text-xs text-zinc-500 mt-1">
                                        Not available on nutrition products for now.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting || items.length === 0}
                            data-testid="checkout-submit-order-button"
                            className="btn-pill btn-primary w-full mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? <ButtonRing label="Opening secure payment…" /> : `Pay Securely — ${formatINR(total)}`}
                        </button>

                        <div className="flex items-center gap-2 text-xs text-zinc-500 justify-center mt-2">
                            <Shield className="w-3.5 h-3.5" /> Secure & encrypted checkout
                        </div>
                    </form>

                    <aside className="lg:col-span-5">
                        <div className="sticky top-28 p-6 bg-bg1 border border-white/10">
                            <h3 className="font-display text-xl uppercase tracking-tight mb-5">Order Summary</h3>
                            <div className="space-y-4 max-h-64 overflow-auto no-scrollbar">
                                {items.length === 0 && <div className="text-zinc-500">Your cart is empty.</div>}
                                {items.map(({ product, quantity }) => (
                                    <div key={product.id} className="flex items-center gap-3" data-testid={`summary-item-${product.slug}`}>
                                        <div className="w-12 h-12 bg-white overflow-hidden shrink-0">
                                            {product.images?.[0] && <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold truncate">{product.name}</div>
                                            <div className="text-xs text-zinc-500">x{quantity}</div>
                                        </div>
                                        <div className="text-sm font-bold">{formatINR(product.price * quantity)}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="border-t border-white/10 mt-5 pt-5">
                                <div className="mono text-[10px] tracking-[0.18em] text-zinc-500 mb-2">COUPON CODE</div>
                                <div className="flex gap-2">
                                    <input
                                        value={coupon}
                                        onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                                        placeholder="RHMFIRST"
                                        data-testid="coupon-input"
                                        className="flex-1 bg-bg0 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-gold"
                                    />
                                    <button type="button" onClick={() => setApplied(coupon.trim())}
                                        data-testid="coupon-apply" className="btn-pill btn-secondary !py-2.5">APPLY</button>
                                </div>
                                {quote?.suggested_offer && !quote?.coupon && (
                                    <button type="button" data-testid="coupon-suggestion"
                                        onClick={() => { setCoupon(quote.suggested_offer.code); setApplied(quote.suggested_offer.code); }}
                                        className="mt-3 w-full flex items-center gap-2 border border-gold/40 bg-gold/5 px-3 py-2.5 text-left">
                                        <Tag className="w-3.5 h-3.5 text-gold" />
                                        <span className="text-xs text-zinc-300">
                                            First order? Tap to apply <span className="text-gold font-bold">{quote.suggested_offer.code}</span> for {quote.suggested_offer.value}% off
                                        </span>
                                    </button>
                                )}
                                {quote?.coupon && (
                                    <div className="mt-3 flex items-center justify-between border border-lime/40 bg-lime/5 px-3 py-2.5">
                                        <span className="mono text-[10px] tracking-[0.14em] text-lime" data-testid="coupon-applied">
                                            {quote.coupon.code} APPLIED
                                        </span>
                                        <button type="button" onClick={() => { setApplied(""); setCoupon(""); }}
                                            data-testid="coupon-remove" className="mono text-[9px] text-zinc-400">REMOVE</button>
                                    </div>
                                )}
                            </div>

                            <div className="border-t border-white/10 mt-5 pt-5 space-y-2 text-sm">
                                <Row label="Subtotal" value={formatINR(subtotal)} />
                                {discount > 0 && <Row label={quote?.bundle ? `${quote.bundle.name} saving` : "Discount"}
                                    value={`- ${formatINR(discount)}`} accent />}
                                <Row label="Shipping" value={shipping === 0 ? "Free" : formatINR(shipping)} />
                                <div className="border-t border-white/10 pt-3 flex justify-between font-bold text-lg">
                                    <span>Total</span>
                                    <span className="text-gold" data-testid="order-total">{formatINR(total)}</span>
                                </div>
                                {quote?.savings > 0 && (
                                    <div className="mono text-[10px] tracking-[0.14em] text-lime pt-1" data-testid="total-savings">
                                        YOU SAVE {formatINR(quote.savings)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}

function Input({ label, name, value, onChange, required, type = "text", testid }) {
    return (
        <label className="block">
            <span className="mono text-[10px] tracking-[0.18em] text-zinc-500 block mb-2">{label}</span>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                data-testid={testid}
                className="w-full bg-bg1 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none transition-all placeholder:text-zinc-600"
            />
        </label>
    );
}

function Row({ label, value, accent }) {
    return (
        <div className="flex justify-between text-zinc-400">
            <span>{label}</span>
            <span className={accent ? "text-lime" : "text-white"}>{value}</span>
        </div>
    );
}
